import { useCallback, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useUploadStore } from "../../store/uploadStore";
import { skipsFileConversionJobMonitoring } from "../../utils/uploadStopBehavior";
import { apiService } from "../../services/apiService";
import { sseService } from "../../services/sseService";
import { resolveSseToken } from "../../services/sseToken";
import { ensureFullGcsPathForVse } from "../../utils/filePathUtils";
import { normalizeConversionStatus } from "../../utils/objectImportProgress";
import { loadStandaloneVseMetadata } from "../../services/editor/standaloneVseMetadata";
import {
  EDITOR_MAX_STORAGE_BYTES,
  EDITOR_MAX_STORAGE_MB,
  billedVseStorageBytes,
} from "../../constants/uploadCapacity";
import type {
  User,
  FileData,
  SSEEvent,
  UploadFlow,
  UploadStatus,
  StandaloneVseMetadataArtifact,
} from "../../types";

/** gs://bucket/path → https://storage.googleapis.com/bucket/path (버킷 공개 시 사용) */
function gcsPathToPublicUrl(gsPath: string | undefined): string {
  if (!gsPath || typeof gsPath !== "string") return "";
  if (!gsPath.startsWith("gs://")) return gsPath;
  return `https://storage.googleapis.com/${gsPath.replace(/^gs:\/\//, "")}`;
}

interface ActiveJobInfo {
  type: "conversion";
  jobId: string;
  pollingTimeoutId?: NodeJS.Timeout;
}

interface UseFileConversionMonitoringProps {
  activeJobsRef: React.MutableRefObject<Map<string, ActiveJobInfo>>;
  tokenRef: React.MutableRefObject<string | undefined>;
  checkPlatformFileFromMainAPI: (
    id: string,
    uid: string,
    modelName: string,
    originalFilename: string,
  ) => Promise<{ signedUrl: string; gcsPath: string; fileName: string } | null>;
  monitorAssetBundle: (
    id: string,
    storagePath: string,
    originalFilename: string,
    startTs: number,
  ) => Promise<void>;
}

type UnknownRecord = Record<string, unknown>;
type CapacityExceededDetail = {
  id: string;
  jobId?: string | null;
  maxCapacityMB: number;
  convertedSizeMB: number;
  remainingMB: number;
};

const CAPACITY_CHECK_FLOWS: UploadFlow[] = ["editor_object_import"];

const CONVERTER_POLLING_LOCKED_STATUSES = new Set<UploadStatus>([
  "processing",
  "bundling",
  "rendering",
  "finalizing",
  "completed",
  "failed",
  "failed_capacity_post_conversion",
  "cancelled",
]);

const toConverterPollingStatus = (
  status: string,
): "converting" | "optimizing" | null => {
  const canonical = normalizeConversionStatus(status);
  if (canonical === "converting" || canonical === "optimizing") {
    return canonical;
  }
  return null;
};

const SIZE_FIELD_CANDIDATES = [
  "converted_size_bytes",
  "converted_file_size_bytes",
  "converted_file_size",
  "output_size_bytes",
  "final_size_bytes",
  "size_bytes",
  "size",
] as const;

const toPositiveNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return null;
};

const getNestedRecord = (
  record: UnknownRecord,
  key: string,
): UnknownRecord | null => {
  const value = record[key];
  if (!value || typeof value !== "object") return null;
  return value as UnknownRecord;
};

const findConvertedSizeBytes = (
  ...records: Array<UnknownRecord | null | undefined>
): number | null => {
  const queue: UnknownRecord[] = records.filter(
    (record): record is UnknownRecord => Boolean(record),
  );

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) break;

    for (const key of SIZE_FIELD_CANDIDATES) {
      const sizeValue = toPositiveNumber(current[key]);
      if (sizeValue != null) return sizeValue;
    }

    const nestedCandidates = [
      getNestedRecord(current, "result"),
      getNestedRecord(current, "details"),
      getNestedRecord(current, "file"),
      getNestedRecord(current, "output"),
      getNestedRecord(current, "meta"),
    ].filter((entry): entry is UnknownRecord => Boolean(entry));
    queue.push(...nestedCandidates);
  }

  return null;
};

/**
 * 파일 변환 모니터링 로직을 처리하는 훅
 */
export const useFileConversionMonitoring = ({
  activeJobsRef,
  tokenRef,
  checkPlatformFileFromMainAPI,
  monitorAssetBundle,
}: UseFileConversionMonitoringProps) => {
  const { user } = useAuth();
  const userToken = (user as User)?.token;
  const setConvertingGlobal = useUploadStore((s) => s.setConverting);
  const setOptimizingGlobal = useUploadStore(
    (s) => s.setOptimizing,
  );
  const setRenderingGlobal = useUploadStore((s) => s.setRendering);
  const completeGlobal = useUploadStore((s) => s.complete);
  const failGlobal = useUploadStore((s) => s.fail);
  const cancelUploadItem = useUploadStore((s) => s.cancel);
  const completionReservationsRef = useRef(new Set<string>());
  const monitorDisposersRef = useRef(new Map<string, () => void>());

  useEffect(() => {
    const disposers = monitorDisposersRef.current;
    return () => {
      disposers.forEach((dispose) => dispose());
      disposers.clear();
    };
  }, []);

  const monitorConversion = useCallback(
    async (
      id: string,
      jobId: string,
      storagePath: string,
      originalFilename: string,
    ): Promise<void> => {
      if (!jobId || completionReservationsRef.current.has(jobId)) return;

      const uploadItem = useUploadStore.getState().items[id];
      if (skipsFileConversionJobMonitoring(uploadItem?.flow)) {
        if (process.env.NODE_ENV === "development") {
          console.log(
            "[useFileConversionMonitoring] skipped: library attach manages its own job lifecycle",
            { id, jobId, flow: uploadItem?.flow },
          );
        }
        return;
      }

      // Avoid duplicate monitoring
      if (
        activeJobsRef.current.has(id) &&
        activeJobsRef.current.get(id)?.type === "conversion"
      ) {
        return;
      }

      if (
        !uploadItem ||
        uploadItem.cancelled ||
        ["completed", "failed", "failed_capacity_post_conversion", "cancelled"].includes(uploadItem.status)
      ) {
        return;
      }

      monitorDisposersRef.current.get(id)?.();
      const activeJob: ActiveJobInfo = { type: "conversion", jobId };
      activeJobsRef.current.set(id, activeJob);
      const admissionExecutionId = useUploadStore.getState().items[id]?.editorImportAdmission?.executionId;
      let disposed = false;
      let selectionTimeout: ReturnType<typeof setTimeout> | undefined;
      const completionReservationKey = jobId || id;
      const hasLiveItem = (): boolean => {
        const item = useUploadStore.getState().items[id];
        return (
          !disposed && Boolean(item) &&
          (!admissionExecutionId || item.editorImportAdmission?.executionId === admissionExecutionId) &&
          (!item.jobId || item.jobId === jobId) && !item.cancelled &&
          !["failed", "failed_capacity_post_conversion", "cancelled"].includes(item.status) &&
          item.editorImportRuntimeState !== "failed"
        );
      };
      const isCurrent = (): boolean => hasLiveItem() &&
        activeJobsRef.current.get(id) === activeJob &&
        useUploadStore.getState().items[id].status !== "completed" &&
        useUploadStore.getState().items[id].editorImportRuntimeState !== "completed";
      const canPoll = (): boolean => isCurrent() &&
        !completionReservationsRef.current.has(completionReservationKey);

      const isDev = process.env.NODE_ENV === "development";

      // The SSE service has one channel per event type. A job cleanup must not
      // disconnect that channel while another conversion still needs it.
      const disconnectIfUnused = (): void => {
        const hasOtherJob = Array.from(activeJobsRef.current.entries()).some(
          ([otherId, info]) => otherId !== id && info.type === "conversion",
        );
        if (!hasOtherJob && sseService.isConnected("file_conversion")) {
          sseService.disconnect("file_conversion");
        }
      };
      const cleanupMonitoringJob = (): void => {
        if (activeJob.pollingTimeoutId) clearTimeout(activeJob.pollingTimeoutId);
        if (activeJobsRef.current.get(id) !== activeJob) return;
        activeJobsRef.current.delete(id);
        disconnectIfUnused();
      };
      monitorDisposersRef.current.set(id, () => {
        disposed = true;
        if (selectionTimeout) clearTimeout(selectionTimeout);
        cleanupMonitoringJob();
      });
      const failMonitoringJob = (uploadId: string, error: string): void => {
        if (!isCurrent()) return;
        cleanupMonitoringJob();
        failGlobal({ id: uploadId, error });
      };
      const failConversionJob = (uploadId: string, error: string): void => {
        const item = useUploadStore.getState().items[uploadId];
        const stage =
          item.status === "uploading" || item.status === "uploaded"
            ? "uploading"
            : item.status === "optimizing" ||
                item.status === "processing" ||
                item.status === "bundling"
              ? "optimizing"
              : "converting";
        useUploadStore.getState().upsertItem({
          id: uploadId,
          conversionFailure: {
            source: "converter",
            status: "failed",
            stage,
            fileName: item.fileName,
          },
        });
        failGlobal({ id: uploadId, error });
      };

      try {
        if (isDev) {
          console.log("[useFileConversionMonitoring] SSE 연결:", { id, jobId });
        }

        const sseToken = await resolveSseToken(userToken || tokenRef.current);
        if (!isCurrent()) {
          cleanupMonitoringJob();
          return;
        }
        if (!sseToken) {
          cleanupMonitoringJob();
          failGlobal({
            id,
            error: "Authentication token missing. Please sign in again.",
          });
          return;
        }

        // SSE delivery is optional; the same job always keeps its DB polling path.
        const connectSse = (): void => sseService.connect(
          "file_conversion",
          sseToken,
          async (data: SSEEvent) => {
            if (!isCurrent()) {
              cleanupMonitoringJob();
              return;
            }
            if (completionReservationsRef.current.has(completionReservationKey)) return;
            // A callback belongs to this monitor attempt, including after disposal.
            const matchingItem = data.job_id === jobId
              ? useUploadStore.getState().items[id]
              : undefined;

            if (data.type === "file_conversion_complete" && matchingItem) {
              if (skipsFileConversionJobMonitoring(matchingItem.flow)) {
                return;
              }

              if (matchingItem.paused) {
                if (isDev) {
                  console.log(
                    "[useFileConversionMonitoring] paused item completion deferred",
                    { id: matchingItem.id, jobId: matchingItem.jobId },
                  );
                }
                return;
              }

              const targetId = matchingItem.id;
              const targetJobId = jobId;
              const result = (data.result || {}) as Record<string, unknown>;
              const resultStatus = normalizeConversionStatus(
                result.status as string,
              );
              if (resultStatus === "paused") {
                cleanupMonitoringJob();
                return;
              }
              if (matchingItem.editorImportAdmission && ["failed", "cancelled"].includes(resultStatus ?? "")) {
                useUploadStore.getState().updateEditorImportAdmission(targetId, admissionExecutionId!, { state: "reconciling" });
                cleanupMonitoringJob();
                return;
              }
              if (resultStatus === "cancelled") {
                cancelUploadItem(targetId);
                cleanupMonitoringJob();
                return;
              }

              if (resultStatus === "failed") {
                const errMsg =
                  (result.error_message as string) || "파일 변환 실패";
                failConversionJob(targetId, errMsg);
                cleanupMonitoringJob();
                return;
              }

              if (
                completionReservationsRef.current.has(completionReservationKey)
              ) {
                return;
              }
              completionReservationsRef.current.add(completionReservationKey);

              // 성공 시 GET /conversion-job/{job_id} 호출해 download_public_url(다운로드용), converted_file_path(GCS 경로) 취득
              try {
                const statusResponse = (await apiService.getConversionJobStatus(
                  targetJobId,
                  sseToken,
                )) as {
                  download_public_url?: string;
                  download_signed_url?: string;
                  converted_file_path?: string;
                  status?: string;
                  error_message?: string;
                  metadata_artifact?: StandaloneVseMetadataArtifact | null;
                } | null;

                if (!isCurrent()) {
                  cleanupMonitoringJob();
                  return;
                }
                if (
                  statusResponse?.status === "failed" ||
                  statusResponse?.error_message
                ) {
                  if (statusResponse?.status === "failed") useUploadStore.getState().releaseEditorImport?.({ id: targetId, jobId, executionId: admissionExecutionId, reason: "remote_terminal" });
                  failConversionJob(
                    targetId,
                    (statusResponse?.error_message as string) || "변환 실패",
                  );
                  cleanupMonitoringJob();
                  return;
                }

                // 다운로드용 URL: public URL 우선, 없으면 기존 signed/폴백
                const fileUrl =
                  statusResponse?.download_public_url ||
                  statusResponse?.download_signed_url ||
                  gcsPathToPublicUrl(statusResponse?.converted_file_path) ||
                  gcsPathToPublicUrl(result.converted_file_path as string) ||
                  "";
                const rawGcsPath =
                  (statusResponse?.converted_file_path as string) ||
                  (result.converted_file_path as string) ||
                  "";
                // object path만 있으면(gs:// 없음) Editor 저장 시 "Invalid bucket name" 오류 → full gs:// 형식 보장
                const gcsPath =
                  ensureFullGcsPathForVse(rawGcsPath) || rawGcsPath;
                const fileName =
                  matchingItem?.fileName || originalFilename || "Untitled";

                const latestBeforeCapacityCheck =
                  useUploadStore.getState().items[targetId];
                const flow = latestBeforeCapacityCheck?.flow;
                const shouldCheckCapacity =
                  flow != null && CAPACITY_CHECK_FLOWS.includes(flow);
                if (shouldCheckCapacity) {
                  const convertedSizeBytes = findConvertedSizeBytes(
                    statusResponse as UnknownRecord,
                    result as UnknownRecord,
                  );
                  if (convertedSizeBytes != null) {
                    const billedCapacityBytes =
                      billedVseStorageBytes(convertedSizeBytes);
                    const snapshotRaw =
                      latestBeforeCapacityCheck?.storageUsageSnapshotBytes;
                    const snapshotUsedBytes =
                      typeof snapshotRaw === "number" &&
                        Number.isFinite(snapshotRaw) &&
                        snapshotRaw >= 0
                        ? snapshotRaw
                        : null;

                    let remainingBytes: number | null = null;
                    if (snapshotUsedBytes != null) {
                      const usedBilledAtSnapshot =
                        billedVseStorageBytes(snapshotUsedBytes);
                      remainingBytes = Math.max(
                        0,
                        EDITOR_MAX_STORAGE_BYTES - usedBilledAtSnapshot,
                      );
                    } else {
                      try {
                        const usage = (await apiService.getStorageUsage(
                          sseToken,
                        )) as { used_bytes?: number } | null;
                        if (!isCurrent()) {
                          cleanupMonitoringJob();
                          return;
                        }
                        const usedBytes = Math.max(
                          0,
                          Number(usage?.used_bytes || 0),
                        );
                        const usedBilled = billedVseStorageBytes(usedBytes);
                        const remainingIfCurrentExcluded =
                          EDITOR_MAX_STORAGE_BYTES - usedBilled;
                        const remainingIfCurrentIncluded =
                          EDITOR_MAX_STORAGE_BYTES -
                          Math.max(
                            0,
                            usedBilled -
                            billedVseStorageBytes(convertedSizeBytes),
                          );
                        if (
                          billedCapacityBytes > remainingIfCurrentExcluded &&
                          billedCapacityBytes > remainingIfCurrentIncluded
                        ) {
                          remainingBytes = Math.min(
                            remainingIfCurrentExcluded,
                            remainingIfCurrentIncluded,
                          );
                        }
                      } catch {
                        // storage usage 조회 실패 시 용량 초과 판정은 skip
                      }
                    }

                    if (
                      remainingBytes != null &&
                      billedCapacityBytes > remainingBytes
                    ) {
                      failGlobal({
                        id: targetId,
                        status: "failed_capacity_post_conversion",
                        error:
                          "Converted file exceeds the remaining space capacity.",
                      });
                      try {
                        const detail: CapacityExceededDetail = {
                          id: targetId,
                          jobId: targetJobId,
                          maxCapacityMB: EDITOR_MAX_STORAGE_MB,
                          convertedSizeMB: billedCapacityBytes / (1024 * 1024),
                          remainingMB: Math.max(
                            0,
                            remainingBytes / (1024 * 1024),
                          ),
                        };
                        window.dispatchEvent(
                          new CustomEvent(
                            "upload:capacityExceededAfterConversion",
                            {
                              detail,
                            },
                          ),
                        );
                      } catch (_) {
                        // ignore
                      }
                      cleanupMonitoringJob();
                      return;
                    }
                  }
                }

                if (!isCurrent()) {
                  cleanupMonitoringJob();
                  return;
                }
                let vseFileMetaInfo;
                try {
                  vseFileMetaInfo =
                    matchingItem.flow === "editor_object_import"
                      ? await loadStandaloneVseMetadata(
                          statusResponse?.metadata_artifact,
                        )
                      : undefined;
                } catch {
                  useUploadStore.getState().releaseEditorImport?.({ id: targetId, jobId, executionId: admissionExecutionId, reason: "remote_terminal" });
                  failConversionJob(targetId, "Import failed.");
                  cleanupMonitoringJob();
                  return;
                }
                const completedFileData = {
                  ...result,
                  ...statusResponse,
                  job_id: targetJobId,
                  jobId: targetJobId,
                  name: fileName,
                  filename: fileName,
                  gcs_path: gcsPath,
                  download_public_url: fileUrl,
                  converted_file_path: gcsPath,
                  vseFileMetaInfo,
                } as FileData;

                if (matchingItem.flow === "editor_object_import") {
                  const isEditorRuntimeAvailable =
                    typeof window !== "undefined" &&
                    window.location.pathname.startsWith("/editor");
                  useUploadStore.getState().upsertItem?.({
                    id: targetId,
                    fileUrl,
                    gcsPath,
                    finalGcsPath: gcsPath,
                    convertedDownloadUrl: fileUrl,
                    jobId: targetJobId || null,
                    platformFiles: [],
                    fileData: completedFileData,
                    editorImportRuntimeState: isEditorRuntimeAvailable
                      ? "result_ready"
                      : "awaiting_user_restore",
                  });
                  setRenderingGlobal({ id: targetId, progress: 60 });
                } else {
                  completeGlobal({
                    id: targetId,
                    fileUrl,
                    gcsPath,
                    finalGcsPath: gcsPath,
                    convertedDownloadUrl: fileUrl,
                    jobId: targetJobId || null,
                    platformFiles: [],
                    fileData: completedFileData,
                  });
                }

                try {
                  window.dispatchEvent(
                    new CustomEvent("upload:workCompleted", {
                      detail: {
                        id: targetId,
                        jobId: targetJobId,
                        status: "completed",
                      },
                    }),
                  );
                  selectionTimeout = setTimeout(() => {
                    if (!hasLiveItem()) return;
                    try {
                      window.dispatchEvent(
                        new CustomEvent("upload:selectNewVSE", {
                          detail: { id: targetId, fileName },
                        }),
                      );
                    } catch (_) {
                      /* ignore */
                    }
                  }, 1000);
                } catch (_) {
                  /* ignore */
                }
                cleanupMonitoringJob();
                return;
              } catch (e) {
                if (isDev)
                  console.warn(
                    "[useFileConversionMonitoring] GET /conversion-job 실패:",
                    e,
                  );
                if (!isCurrent()) {
                  cleanupMonitoringJob();
                  return;
                }
                completionReservationsRef.current.delete(completionReservationKey);
                schedulePolling();
                return;
              }
            }
          },
          (error: Error) => {
            if (!isCurrent()) {
              cleanupMonitoringJob();
              return;
            }
            console.warn("[useFileConversionMonitoring] SSE delivery unavailable; continuing job polling:", error);
          },
          jobId,
          {
            owner: "upload-monitor",
            tokenProvider: () => resolveSseToken(userToken || tokenRef.current),
          },
        );

        try {
          connectSse();
        } catch (error) {
          if (isDev) console.warn("[useFileConversionMonitoring] SSE initialization failed; continuing job polling:", error);
        }

        const schedulePolling = (): void => {
          if (!isCurrent()) {
            cleanupMonitoringJob();
            return;
          }
          if (!canPoll()) return;
          if (activeJob.pollingTimeoutId) clearTimeout(activeJob.pollingTimeoutId);
          activeJob.pollingTimeoutId = setTimeout(checkStatus, 2000);
        };

        const checkStatus = async (): Promise<void> => {
          if (!isCurrent()) {
            cleanupMonitoringJob();
            return;
          }
          if (!canPoll()) return;
          try {
            const store = useUploadStore.getState();
            const item = store.items[id];

            if (item.paused) {
              schedulePolling();
              return;
            }

            const statusResponse = (await apiService.getConversionJobStatus(
              jobId,
              sseToken,
            )) as {
              progress?: number | string;
              status?: string;
              error_message?: string;
            } | null;
            if (!isCurrent()) {
              cleanupMonitoringJob();
              return;
            }
            if (!canPoll()) return;
            const progressValue = parseInt(
              String(statusResponse?.progress || 0),
            );
            const statusValue = normalizeConversionStatus(
              statusResponse?.status,
            );
            const latestItem = useUploadStore.getState().items[id];

            if (latestItem.paused || latestItem.status === "paused") {
              schedulePolling();
              return;
            }

            const isCompletedResponse =
              statusValue === "completed" ||
              statusValue === "success" ||
              (!isNaN(progressValue) && progressValue >= 100);

            if (statusValue === "cancelled") {
              useUploadStore.getState().releaseEditorImport?.({ id, jobId, executionId: admissionExecutionId, reason: "remote_terminal" });
              cancelUploadItem(id);
              cleanupMonitoringJob();
              return;
            }
            if (statusValue === "failed") {
              const errMsg =
                (statusResponse as { error_message?: string })?.error_message ||
                "Conversion failed";
              console.error("[useFileConversionMonitoring] ❌ 변환 실패:", {
                jobId,
                error: errMsg,
              });
              useUploadStore.getState().releaseEditorImport?.({ id, jobId, executionId: admissionExecutionId, reason: "remote_terminal" });
              failConversionJob(id, errMsg);
              cleanupMonitoringJob();
              return;
            }

            // SSE나 앞선 polling이 이미 후속 단계로 전환했다면 늦은 응답을 폐기한다.
            if (
              !isCompletedResponse &&
              CONVERTER_POLLING_LOCKED_STATUSES.has(latestItem.status)
            ) {
              cleanupMonitoringJob();
              return;
            }

            if (isCompletedResponse) {
              if (
                completionReservationsRef.current.has(completionReservationKey)
              ) {
                return;
              }
              completionReservationsRef.current.add(completionReservationKey);

              const jobResponse = statusResponse as {
                download_public_url?: string;
                download_signed_url?: string;
                converted_file_path?: string;
                error_message?: string;
                metadata_artifact?: StandaloneVseMetadataArtifact | null;
              };
              const fileUrl =
                jobResponse?.download_public_url ||
                jobResponse?.download_signed_url ||
                gcsPathToPublicUrl(jobResponse?.converted_file_path) ||
                "";
              const rawGcsPath =
                (jobResponse?.converted_file_path as string) || "";
              const gcsPath = ensureFullGcsPathForVse(rawGcsPath) || rawGcsPath;
              const store = useUploadStore.getState();
              const item = store.items[id];
              if (item?.cancelled) {
                cleanupMonitoringJob();
                return;
              }
              const fileName = item?.fileName || originalFilename || "Untitled";

              const shouldCheckCapacity =
                item?.flow != null && CAPACITY_CHECK_FLOWS.includes(item.flow);
              if (shouldCheckCapacity) {
                const convertedSizeBytes = findConvertedSizeBytes(
                  statusResponse as UnknownRecord,
                );
                if (convertedSizeBytes != null) {
                  const billedCapacityBytes =
                    billedVseStorageBytes(convertedSizeBytes);
                  const snapshotRaw = item?.storageUsageSnapshotBytes;
                  const snapshotUsedBytes =
                    typeof snapshotRaw === "number" &&
                      Number.isFinite(snapshotRaw) &&
                      snapshotRaw >= 0
                      ? snapshotRaw
                      : null;
                  let remainingBytes: number | null = null;

                  if (snapshotUsedBytes != null) {
                    const usedBilledAtSnapshot =
                      billedVseStorageBytes(snapshotUsedBytes);
                    remainingBytes = Math.max(
                      0,
                      EDITOR_MAX_STORAGE_BYTES - usedBilledAtSnapshot,
                    );
                  } else {
                    try {
                      const usage = (await apiService.getStorageUsage(
                        sseToken,
                      )) as { used_bytes?: number } | null;
                      if (!isCurrent()) {
                        cleanupMonitoringJob();
                        return;
                      }
                      const usedBytes = Math.max(
                        0,
                        Number(usage?.used_bytes || 0),
                      );
                      const usedBilled = billedVseStorageBytes(usedBytes);
                      const remainingIfCurrentExcluded =
                        EDITOR_MAX_STORAGE_BYTES - usedBilled;
                      const remainingIfCurrentIncluded =
                        EDITOR_MAX_STORAGE_BYTES -
                        Math.max(
                          0,
                          usedBilled -
                          billedVseStorageBytes(convertedSizeBytes),
                        );
                      if (
                        billedCapacityBytes > remainingIfCurrentExcluded &&
                        billedCapacityBytes > remainingIfCurrentIncluded
                      ) {
                        remainingBytes = Math.min(
                          remainingIfCurrentExcluded,
                          remainingIfCurrentIncluded,
                        );
                      }
                    } catch {
                      // storage usage 조회 실패 시 용량 초과 판정은 skip
                    }
                  }

                  if (
                    remainingBytes != null &&
                    billedCapacityBytes > remainingBytes
                  ) {
                    failGlobal({
                      id,
                      status: "failed_capacity_post_conversion",
                      error:
                        "Converted file exceeds the remaining space capacity.",
                    });
                    try {
                      const detail: CapacityExceededDetail = {
                        id,
                        jobId,
                        maxCapacityMB: EDITOR_MAX_STORAGE_MB,
                        convertedSizeMB: billedCapacityBytes / (1024 * 1024),
                        remainingMB: Math.max(
                          0,
                          remainingBytes / (1024 * 1024),
                        ),
                      };
                      window.dispatchEvent(
                        new CustomEvent(
                          "upload:capacityExceededAfterConversion",
                          {
                            detail,
                          },
                        ),
                      );
                    } catch (_) {
                      // ignore
                    }
                    cleanupMonitoringJob();
                    return;
                  }
                }
              }

              if (!isCurrent()) {
                cleanupMonitoringJob();
                return;
              }
              let vseFileMetaInfo;
              try {
                vseFileMetaInfo =
                  item?.flow === "editor_object_import"
                    ? await loadStandaloneVseMetadata(
                        jobResponse.metadata_artifact,
                      )
                    : undefined;
              } catch {
                useUploadStore.getState().releaseEditorImport?.({ id, jobId, executionId: admissionExecutionId, reason: "remote_terminal" });
                failConversionJob(id, "Import failed.");
                cleanupMonitoringJob();
                return;
              }
              const completedFileData = {
                ...statusResponse,
                job_id: jobId,
                jobId,
                name: fileName,
                filename: fileName,
                gcs_path: gcsPath,
                download_public_url: fileUrl,
                converted_file_path: gcsPath,
                vseFileMetaInfo,
              } as FileData;

              if (item?.flow === "editor_object_import") {
                useUploadStore.getState().upsertItem?.({
                  id,
                  fileUrl,
                  gcsPath,
                  finalGcsPath: gcsPath,
                  convertedDownloadUrl: fileUrl,
                  jobId,
                  platformFiles: [],
                  fileData: completedFileData,
                });
                setRenderingGlobal({ id, progress: 60 });
              } else {
                completeGlobal({
                  id,
                  fileUrl,
                  gcsPath,
                  finalGcsPath: gcsPath,
                  convertedDownloadUrl: fileUrl,
                  jobId,
                  platformFiles: [],
                  fileData: completedFileData,
                });
              }

              try {
                window.dispatchEvent(
                  new CustomEvent("upload:workCompleted", {
                    detail: { id, jobId, status: "completed" },
                  }),
                );
                selectionTimeout = setTimeout(() => {
                  if (!hasLiveItem()) return;
                  try {
                    window.dispatchEvent(
                      new CustomEvent("upload:selectNewVSE", {
                        detail: { id, fileName: originalFilename },
                      }),
                    );
                  } catch (_) {
                    /* ignore */
                  }
                }, 1000);
              } catch (_) {
                /* ignore */
              }
              cleanupMonitoringJob();
              return;
            }

            const pollingStatus = toConverterPollingStatus(statusValue);
            const isRegressiveConverterStatus =
              latestItem.status === "optimizing" &&
              pollingStatus === "converting";
            if (
              pollingStatus === "converting" &&
              !isRegressiveConverterStatus
            ) {
              setConvertingGlobal({ id, jobId, storagePath });
            } else if (pollingStatus === "optimizing") {
              setOptimizingGlobal({ id });
            }

            schedulePolling();
          } catch (pollErr) {
            const errorMessage =
              pollErr instanceof Error ? pollErr.message : "Unknown error";
            if (isDev) {
              console.warn(
                "[useFileConversionMonitoring] 폴링 에러 (재시도):",
                { jobId, error: errorMessage },
              );
            }
            schedulePolling();
          }
        };

        checkStatus();
      } catch (e) {
        const message =
          e instanceof Error && e.message.trim().length > 0
            ? e.message.trim()
            : "Conversion failed. Unable to start conversion tracking. Please try again.";
        console.error(
          "[useFileConversionMonitoring] monitoring start failed:",
          e,
        );
        failMonitoringJob(id, message);
      }
    },
    [
      userToken,
      completeGlobal,
      failGlobal,
      cancelUploadItem,
      setConvertingGlobal,
      setOptimizingGlobal,
      setRenderingGlobal,
      activeJobsRef,
      tokenRef,
    ],
  );

  return { monitorConversion };
};
